/* size guide modal - slide-up state, right-column anchor, IN/CM toggle

   The bf-size-charts app animates .scr-modal by toggling inline
   opacity/visibility/transform on that one element - no class, no display swap
   - so the CSS has nothing to key a slide-up off. This mirrors the app's inline
   visibility onto a real class. .scr-modal does not exist until the app first
   builds it (it waits for its own button to scroll into view), hence the
   document-wide watch rather than a single query. */

(function () {
  'use strict';

  var OPEN_CLASS = 'yg-size-guide--open';
  var COLUMN_SELECTOR =
    '.product-information .product-details > .group-block > .group-block-content > .group-block:has(product-form-component)';
  var DESKTOP_QUERY = '(min-width: 750px)';
  /* A comfortable width for the card's own content, NOT the right column's:
     that column measured as narrow as ~408px in testing, which left the card
     with both a horizontal and a vertical scrollbar. The column only anchors
     where the card's right edge sits. */
  var TARGET_WIDTH = 480;
  /* The app's chart data on this store runs from 3 columns to 7, in either
     orientation, so the card is sized to what the table's nowrap content needs
     between these bounds. */
  var MIN_WIDTH = 440;
  var MAX_WIDTH = 620;
  /* Card padding plus .scr.basic's padding each side, plus room for that
     box's vertical scrollbar, which appears on the taller charts. */
  var CARD_CHROME = 2 * (14 + 22) + 20;
  /* Generous estimate of the card's full height, used to decide how far up to
     pull the anchor on shorter viewports. A flat floor on .scr.basic alone did
     not account for the card's own padding and cut the table off. */
  var CONTENT_HEIGHT = 600;
  /* matches .scr-modal's own padding in yg-custom.css - .scr.basic's
     max-height has to leave this much room inside the card */
  var MODAL_VERTICAL_PADDING = 64;

  var desktop = window.matchMedia(DESKTOP_QUERY);
  var attached = null;
  var wasOpen = false;
  var resizeTimer = null;
  var scrollTimer = null;

  function isOpen(modal) {
    return modal.style.visibility === 'visible';
  }

  function headerHeight() {
    var value = getComputedStyle(document.body).getPropertyValue('--header-height');
    var parsed = parseFloat(value);
    return isNaN(parsed) ? 80 : parsed;
  }

  /**
   * Widest the table can ever want to be, measured off the worst case so
   * switching units never resizes the card.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function naturalTableWidth(modal) {
    var table = modal.querySelector('.scr-table');
    if (!table) return 0;

    modal.style.setProperty('--yg-sg-fit', '1');

    var restore = [];
    table.querySelectorAll('td[data-cm]').forEach(function (cell) {
      var widest = cell.getAttribute('data-cm');
      if (!widest || widest.length <= cell.textContent.length) return;
      restore.push([cell, cell.textContent]);
      cell.textContent = widest;
    });

    modal.classList.add('yg-sg-measure');
    var width = table.getBoundingClientRect().width;
    modal.classList.remove('yg-sg-measure');

    restore.forEach(function (pair) {
      pair[0].textContent = pair[1];
    });

    return width;
  }

  /**
   * Writes the right-column buy-box's bounds onto the modal as custom
   * properties, so CSS can anchor there rather than where the app centres.
   * @param {HTMLElement} modal
   */
  function position(modal) {
    if (!desktop.matches) return;

    var column = document.querySelector(COLUMN_SELECTOR);
    if (!column) return;

    var rect = column.getBoundingClientRect();
    if (!rect.width) return;

    var addToCart = column.querySelector('button[name="add"]');
    var anchorRect = addToCart ? addToCart.getBoundingClientRect() : rect;

    var margin = 16;
    var minTop = headerHeight() + 16;
    /* Anchor at the add-to-cart button, but never leave less than
       CONTENT_HEIGHT below it - pull the anchor up rather than compress the
       card when the button sits low on a shorter viewport. */
    var idealTop = Math.min(anchorRect.top, window.innerHeight - CONTENT_HEIGHT - margin);
    var top = Math.max(minTop, idealTop);

    var right = Math.max(16, window.innerWidth - rect.right);
    var room = Math.min(MAX_WIDTH, window.innerWidth - right - 16);
    var wanted = naturalTableWidth(modal);
    var width = Math.min(room, Math.max(MIN_WIDTH, wanted ? wanted + CARD_CHROME : TARGET_WIDTH));
    /* the header sits above .scr.basic, so its band comes out of the scroller */
    var maxHeight = Math.max(
      240,
      window.innerHeight - top - margin - MODAL_VERTICAL_PADDING - headerBand(modal)
    );

    modal.style.setProperty('--yg-sg-top', top + 'px');
    modal.style.setProperty('--yg-sg-right', right + 'px');
    modal.style.setProperty('--yg-sg-width', width + 'px');
    modal.style.setProperty('--yg-sg-max-h', maxHeight + 'px');
  }

  /* table fit - nowrap columns, scaled type, scroll fallback */

  /**
   * Moves the app's table into a scroll frame so one too wide for the card
   * scrolls instead of being clipped by .scr.basic.
   * @param {HTMLElement} modal
   * @returns {HTMLElement|null}
   */
  function wrapTable(modal) {
    var table = modal.querySelector('.scr-table');
    if (!table) return null;

    var viewport = table.parentNode;
    if (viewport && viewport.classList && viewport.classList.contains('yg-sg-table-viewport')) {
      return viewport.parentNode;
    }

    var frame = document.createElement('div');
    frame.className = 'yg-sg-table-scroll';
    viewport = document.createElement('div');
    viewport.className = 'yg-sg-table-viewport';

    table.parentNode.insertBefore(frame, table);
    frame.appendChild(viewport);
    viewport.appendChild(table);

    viewport.addEventListener(
      'scroll',
      function () {
        markOverflow(frame);
      },
      { passive: true }
    );

    return frame;
  }

  /* header - title lifted clear of the scroll boundary */

  /**
   * The close button is absolute against the card while the title sits inside
   * .scr.basic, so content scrolled up under it. Reserving space inside the
   * scroller cannot fix that - a scroller's own padding-top scrolls away with
   * the content - so the scroller has to start below the button instead.
   * @param {HTMLElement} modal
   */
  function buildHeader(modal) {
    var panel = modal.querySelector('.scr.basic');
    if (!panel) return;

    var header = modal.querySelector('.yg-sg-header');
    var title = panel.querySelector('h2');

    if (title) {
      if (!header) {
        header = document.createElement('div');
        header.className = 'yg-sg-header';
        modal.insertBefore(header, panel);
      }
      while (header.firstChild) header.removeChild(header.firstChild);
      header.appendChild(title);
    }

    if (header) {
      modal.style.setProperty('--yg-sg-head-h', headerBand(modal) + 'px');
    }
  }

  /**
   * The header's own height plus the margin separating it from the scroller;
   * both have to be counted or the card ends up that margin too tall. Not
   * named headerHeight: that is already the site-header helper.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function headerBand(modal) {
    var bar = modal.querySelector('.yg-sg-header');
    if (!bar) return 0;

    var gap = parseFloat(getComputedStyle(bar).marginBottom);
    return bar.getBoundingClientRect().height + (isNaN(gap) ? 0 : gap);
  }

  /* scroll state - bottom fade and the fading scrollbar */

  /**
   * @param {HTMLElement} modal
   */
  function updateScrollFade(modal) {
    /* one .scr panel per chart - the app appends one per child chart */
    modal.querySelectorAll('.scr.basic').forEach(function (panel) {
      var slack = panel.scrollHeight - panel.clientHeight;
      panel.classList.toggle('has-more-below', slack > 1 && panel.scrollTop < slack - 1);
    });
  }

  /**
   * @param {HTMLElement} modal
   */
  function bindScroll(modal) {
    modal.querySelectorAll('.scr.basic').forEach(function (panel) {
      if (panel.hasAttribute('data-yg-scroll')) return;
      panel.setAttribute('data-yg-scroll', '');

      panel.addEventListener(
        'scroll',
        function () {
          updateScrollFade(modal);
          panel.classList.add('is-scrolling');
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(function () {
            panel.classList.remove('is-scrolling');
          }, 700);
        },
        { passive: true }
      );

      /* the how-to-measure diagram is usually still loading on first open */
      panel.querySelectorAll('img').forEach(function (image) {
        if (image.complete) return;
        image.addEventListener('load', function () {
          updateScrollFade(modal);
        }, { once: true });
      });
    });
  }

  /**
   * @param {HTMLElement} frame
   */
  function markOverflow(frame) {
    var viewport = frame.querySelector('.yg-sg-table-viewport');
    if (!viewport) return;

    var slack = viewport.scrollWidth - viewport.clientWidth;
    frame.classList.toggle('is-overflowing', slack > 1);
    frame.classList.toggle('is-at-end', viewport.scrollLeft >= slack - 1);
  }

  /**
   * Scales the table's type and cell padding down until its nowrap content
   * fits, stopping at --yg-sg-fit-min. The rest is left to the scroll frame.
   * @param {HTMLElement} modal
   */
  function fitTable(modal) {
    var frame = modal.querySelector('.yg-sg-table-scroll');
    var viewport = modal.querySelector('.yg-sg-table-viewport');
    if (!frame || !viewport) return;

    modal.style.setProperty('--yg-sg-fit', '1');

    var available = viewport.clientWidth;
    var wanted = viewport.scrollWidth;
    if (!available || !wanted) return;

    if (wanted > available) {
      var floor = parseFloat(getComputedStyle(modal).getPropertyValue('--yg-sg-fit-min')) || 0.78;
      var scale = Math.max(floor, available / wanted);
      modal.style.setProperty('--yg-sg-fit', String(scale));

      /* glyph widths do not scale linearly with font-size - one correction
         pass off the real measurement settles the rounding */
      if (scale > floor && viewport.scrollWidth > viewport.clientWidth) {
        scale = Math.max(floor, scale * (viewport.clientWidth / viewport.scrollWidth));
        modal.style.setProperty('--yg-sg-fit', String(scale));
      }
    }

    markOverflow(frame);
  }

  /* tip banner - flatten merchant rich text into plain <p> */

  /**
   * descriptionTop is authored in the app's rich-text editor, so its top level
   * is not reliably <p>: found live, one chart is a whitespace-only <p> then a
   * <blockquote>. Only <p> gets the pill, so flatten every top-level node to a
   * plain <p> and drop the blank ones.
   * @param {HTMLElement} modal
   */
  function normaliseTip(modal) {
    var tip = modal.querySelector('.top-description');
    if (!tip || tip.hasAttribute('data-yg-tip')) return;
    tip.setAttribute('data-yg-tip', 'ready');

    Array.prototype.slice.call(tip.childNodes).forEach(function (node) {
      if (node.nodeType === 3) {
        if (!node.textContent.trim()) {
          tip.removeChild(node);
          return;
        }
        var wrap = document.createElement('p');
        tip.replaceChild(wrap, node);
        wrap.appendChild(node);
        return;
      }

      if (node.nodeType !== 1 || node.tagName === 'P') return;

      /* unwrap rather than nest - a <blockquote>/<div> carries the merchant's
         own inline margins and colours, which is what broke the pill */
      var para = document.createElement('p');
      while (node.firstChild) para.appendChild(node.firstChild);
      tip.replaceChild(para, node);
    });

    Array.prototype.slice.call(tip.children).forEach(function (child) {
      if (child.tagName !== 'P') return;
      if (!child.textContent.trim() && !child.querySelector('img')) tip.removeChild(child);
    });

    if (!tip.textContent.trim() && !tip.querySelector('img')) {
      tip.setAttribute('data-yg-tip', 'empty');
    }
  }

  function layout(modal) {
    normaliseTip(modal);
    buildHeader(modal);
    buildUnitToggle(modal);
    wrapTable(modal);
    bindScroll(modal);
    position(modal);
    fitTable(modal);
    updateScrollFade(modal);
  }

  function sync(modal) {
    var open = isOpen(modal);
    modal.classList.toggle(OPEN_CLASS, open);

    if (open && !wasOpen) layout(modal);

    wasOpen = open;
  }

  var styleObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) sync(mutations[i].target);
  });

  function attach(modal) {
    if (attached === modal) return;
    attached = modal;
    wasOpen = false;
    styleObserver.disconnect();
    sync(modal);
    styleObserver.observe(modal, { attributes: true, attributeFilter: ['style'] });
  }

  var bodyObserver = new MutationObserver(function () {
    if (attached && attached.isConnected) return;
    var modal = document.querySelector('.scr-modal');
    if (modal) attach(modal);
  });

  bodyObserver.observe(document.documentElement, { childList: true, subtree: true });

  var existing = document.querySelector('.scr-modal');
  if (existing) attach(existing);

  window.addEventListener('resize', function () {
    if (!attached || !wasOpen) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      position(attached);
      fitTable(attached);
      updateScrollFade(attached);
    }, 120);
  });

  /* size chart opener — drag guard */
  var OPENER_SELECTOR = '.scr-open-size-chart';
  var DRAG_THRESHOLD = 10;
  var press = null;

  function touchPoint(event) {
    var touch = event.changedTouches && event.changedTouches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  function movedPast(point) {
    return !point || Math.hypot(point.x - press.x, point.y - press.y) > DRAG_THRESHOLD;
  }

  window.addEventListener(
    'touchstart',
    function (event) {
      if (event.touches.length > 1) {
        if (press) press.moved = true;
        return;
      }
      var onOpener = event.target instanceof Element && event.target.closest(OPENER_SELECTOR);
      var point = touchPoint(event);
      press = onOpener && point ? { x: point.x, y: point.y, moved: false } : null;
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'touchmove',
    function (event) {
      if (press && !press.moved && movedPast(touchPoint(event))) press.moved = true;
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'touchend',
    function (event) {
      if (!press) return;
      var dragged = press.moved || movedPast(touchPoint(event));
      if (event.touches.length === 0) press = null;
      if (!dragged) return;
      if (event.target instanceof Element && event.target.closest(OPENER_SELECTOR)) {
        event.stopImmediatePropagation();
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'touchcancel',
    function () {
      press = null;
    },
    { capture: true, passive: true }
  );

  /* unit toggle - injected once per modal, real per-product data */

  /**
   * Every text node under the footnote that names the unit, with its original
   * string. Assigning textContent replaces the whole subtree with one text
   * node, which destroyed the "How to measure" image seven of this store's
   * sixteen charts carry - so rewrite node by node and leave structure alone.
   * @param {HTMLElement|null} footnote
   * @returns {{node: Text, original: string}[]}
   */
  function collectUnitText(footnote) {
    var entries = [];
    if (!footnote) return entries;

    /* The whole footnote's text is assembled first because the merchant's
       editor splits sentences across text nodes at will, so the quantity test
       below cannot be answered from inside one node. */
    var walker = document.createTreeWalker(footnote, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [];
    var full = '';
    var node;
    while ((node = walker.nextNode())) {
      nodes.push({ node: node, original: node.nodeValue, start: full.length });
      full += node.nodeValue;
    }

    nodes.forEach(function (entry) {
      if (!/\binch(es)?\b/i.test(entry.original)) return;
      entry.converted = toCentimetres(entry.original, full, entry.start);
      if (entry.converted !== entry.original) entries.push(entry);
    });

    return entries;
  }

  /**
   * @param {string} text one text node's value
   * @param {string} full the whole footnote's text
   * @param {number} start where `text` begins inside `full`
   * @returns {string}
   */
  function toCentimetres(text, full, start) {
    return text.replace(/\b(inches|inch)\b/gi, function (unit, _group, index) {
      /* a quantity in the merchant's own prose is a real measurement, not a
         label for the table's units - relabelling it would be wrong by 2.54x */
      var before = full.slice(0, start + index).replace(/[\s ]+$/, '');
      if (/\d$/.test(before)) return unit;

      var word = unit.toLowerCase() === 'inch' ? 'centimeter' : 'centimeters';
      if (unit === unit.toUpperCase()) return word.toUpperCase();
      if (unit.charAt(0) === unit.charAt(0).toUpperCase()) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    });
  }

  /**
   * @param {number} inches
   * @returns {string}
   */
  function inchesToCm(inches) {
    var rounded = Math.round(inches * 2.54 * 10) / 10;
    var whole = Math.round(rounded);
    return whole === rounded ? String(whole) : String(rounded);
  }

  /**
   * @param {HTMLElement} modal
   */
  function buildUnitToggle(modal) {
    if (modal.querySelector('.yg-sg-unit-toggle')) return;

    var table = modal.querySelector('.scr-table');
    if (!table) return;

    /* header row cells are <th>, never <td>, so this skips S/M/L/XL/XXL
       whether or not the table has a real <thead> */
    var cells = table.querySelectorAll('td:not(:first-child)');
    if (!cells.length) return;

    var any = false;
    cells.forEach(function (cell) {
      var raw = cell.textContent.trim();
      var value = parseFloat(raw);
      if (!raw || isNaN(value)) return;
      cell.setAttribute('data-in', raw);
      cell.setAttribute('data-cm', inchesToCm(value));
      any = true;
    });
    if (!any) return;

    var footnoteText = collectUnitText(modal.querySelector('.bottom-description'));

    var toggle = document.createElement('div');
    toggle.className = 'yg-sg-unit-toggle';
    toggle.setAttribute('role', 'group');
    toggle.setAttribute('aria-label', 'Measurement unit');

    ['in', 'cm'].forEach(function (unit) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'yg-sg-unit-btn' + (unit === 'in' ? ' is-active' : '');
      button.dataset.unit = unit;
      button.setAttribute('aria-pressed', unit === 'in' ? 'true' : 'false');
      button.textContent = unit.toUpperCase();

      button.addEventListener('click', function () {
        toggle.querySelectorAll('.yg-sg-unit-btn').forEach(function (other) {
          var active = other === button;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        cells.forEach(function (cell) {
          var value = cell.getAttribute('data-' + unit);
          if (value != null) cell.textContent = value;
        });

        footnoteText.forEach(function (entry) {
          entry.node.nodeValue = unit === 'cm' ? entry.converted : entry.original;
        });

        fitTable(modal);
        updateScrollFade(modal);
      });

      toggle.appendChild(button);
    });

    table.parentNode.insertBefore(toggle, table);
  }
})();
